// import { useState, useEffect, useCallback, useRef } from "react";
// import axios from "axios";
// import isEqual from "lodash.isequal";

// const backendUrl = process.env.REACT_APP_BACKEND_URL;

// export default function useAdminData({
//   currentUser,
//   handleLogout,
//   prevEventsRef,
//   didFetchRef,
//   audioRef,
//   activeTab,
//   selectedDevices,
//   setDeviceAddresses,
//   skipFetch = false, 
// }) {
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [users, setUsers] = useState([]);
//   const [devices, setDevices] = useState([]);
//   const [devicesOnMap, setDevicesOnMap] = useState([]);
//   const [deviceCounts, setDeviceCounts] = useState({ total: 0, online: 0, offline: 0 });
//   const [deviceEvents, setDeviceEvents] = useState([]);
//   const [eventLoading, setEventLoading] = useState(false);
//   const [eventError, setEventError] = useState(null);
//   const [assetsData, setAssetsData] = useState([]);
//   const [isAssetsLoading, setIsAssetsLoading] = useState(false);
//   const [assetError, setAssetError] = useState(null);
//   const [editingUser, setEditingUser] = useState(null);

//   const lastEventRef = useRef(null);

//   const playEventSound = useCallback(() => { 
//     if (audioRef.current) {
//       audioRef.current.play().catch((err) =>
//         console.warn("Sound couldn't play (not yet allowed):", err.message)
//       );
//     }
//   }, [audioRef]); 

//   // --- Data Fetching Functions ---
//   const fetchAdminData = useCallback(async () => {
//     if (skipFetch) {
//         setLoading(false);
//         return;
//     }
    
//     const token = localStorage.getItem("token");
//     if (!token) {
//       handleLogout();
//       return;
//     }
//     setLoading(true);
//     setError(null);
//     setIsAssetsLoading(true);
//     setAssetError(null);
//     try {
//       const config = { headers: { "x-auth-token": token } };
//       const requiredDeviceFields = "name model vehicleType imei msdn imsi deviceToken deviceType status assignedToUser assignedToViewers geofences lastDataReceivedAt";

//       const [usersRes, devicesRes, assetsRes] = await Promise.all([
//         axios.get(`${backendUrl}/admin/users`, config),
//         axios.get(`${backendUrl}/devices?fields=${requiredDeviceFields}`, config),
//         axios.get(`${backendUrl}/devices/assets`, config),
//       ]);

//       setUsers(usersRes.data);
//       setDevices(devicesRes.data);
//       setAssetsData(assetsRes.data.data);
//     } catch (err) {
//       console.error("Error fetching admin data:", err.response ? err.response.data : err.message);
//       const errorMessage = err.response?.data?.msg || "Failed to load admin data.";
//       setError(errorMessage);
//       setAssetError(errorMessage);
//       if (err.response?.status === 401 || err.response?.status === 403) {
//         handleLogout();
//       }
//     } finally {
//       setLoading(false);
//       setIsAssetsLoading(false);
//     }
//   }, [handleLogout, skipFetch]);

//   const fetchAllAssets = useCallback(async () => {
//     if (skipFetch) return;
//     setIsAssetsLoading(true);
//     setAssetError(null);
//     try {
//         const token = localStorage.getItem("token");
//         if (!token) { handleLogout(); return; }
//         const config = { headers: { "x-auth-token": token } };
//         const response = await axios.get(`${backendUrl}/devices/assets`, config);
//         setAssetsData(response.data.data);
//     } catch (err) {
//         console.error("Error fetching assets:", err.response ? err.response.data : err.message);
//         setAssetError(err.response?.data?.msg || "Failed to load assets.");
//         if (err.response?.status === 401 || err.response?.status === 403) { handleLogout(); }
//     } finally {
//         setIsAssetsLoading(false);
//     }
//   }, [handleLogout, skipFetch]);

//   const fetchLiveData = useCallback(async () => {
//     if (skipFetch) return;
//     try {
//         const token = localStorage.getItem("token");
//         if (!token) { handleLogout(); return; }
//         const config = { headers: { "x-auth-token": token } };
//         const requiredDeviceFields = "name model vehicleType imei deviceToken deviceType status assignedToUser assignedToViewers lastDataReceivedAt liveData geofences";

//         const devicesRes = await axios.get(`${backendUrl}/devices?fields=${requiredDeviceFields}`, config);

//         const processedDevices = devicesRes.data.map((device) => {
//             let currentLocation = null;
//             if (device.liveData && typeof device.liveData.latitude === "number" && typeof device.liveData.longitude === "number") {
//                 currentLocation = {
//                     latitude: device.liveData.latitude,
//                     longitude: device.liveData.longitude,
//                     timestamp: device.liveData.timestamp || new Date(),
//                     speed: device.liveData.speed || 0,
//                 };
//             }
//             return { ...device, currentLocation };
//         });

//         const total = processedDevices.length;
//         const online = processedDevices.filter(
//             device => device.status && device.status.toLowerCase().trim() === 'online'
//         ).length;
//         const offline = total - online;

//         setDeviceCounts({ total, online, offline });
//         setDevicesOnMap(processedDevices);
//     } catch (err) {
//         console.error("Error fetching live data:", err.response ? err.response.data : err.message);
//         const errorMessage = err.response?.data?.msg || "Failed to load live data.";
//         setError(errorMessage);
//     }
//   }, [handleLogout, skipFetch]);

//   const fetchAllDeviceEvents = useCallback(
//     async (isInitialFetch = false) => {
//       if (skipFetch) return;

//       const token = localStorage.getItem("token");
//       if (!token) { handleLogout(); return; }
//       if (isInitialFetch) setEventLoading(true);
//       setEventError(null);

//       try {
//           const config = { headers: { "x-auth-token": token } };
//           const response = await axios.get(`${backendUrl}/devices/events`, config);
//           const allEvents = response.data;

//           const flattenedEvents = allEvents.flatMap((device) =>
//               device.events.map((event) => ({ ...event, imei: device.imei, deviceName: device.deviceName, }))
//           );
//           flattenedEvents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
//           const latestEvent = flattenedEvents[0];

//           if (
//               latestEvent &&
//               (!lastEventRef.current || lastEventRef.current.timestamp !== latestEvent.timestamp || lastEventRef.current.imei !== latestEvent.imei)
//           ) {
//               playEventSound();
//               lastEventRef.current = { timestamp: latestEvent.timestamp, imei: latestEvent.imei, };
//           }

//           if (!isEqual(allEvents, prevEventsRef.current)) {
//               prevEventsRef.current = allEvents;
//               setDeviceEvents(allEvents);
//           }
//           if (isInitialFetch) setEventLoading(false);
//       } catch (err) {
//           console.error("Error fetching all device events:", err);
//           setEventError(err.response ? err.response.data.msg || "Failed to load device events." : "Network error. Please try again.");
//           if (isInitialFetch) setEventLoading(false);
//       }
//     },
//     [handleLogout, prevEventsRef, playEventSound, skipFetch]
//   );

//   // --- Initial Fetch useEffect ---
//   useEffect(() => {
//     if (currentUser && currentUser.role === 'admin' && !didFetchRef.current) {
//       fetchAdminData();
//       fetchAllDeviceEvents(true);
//       fetchLiveData();
//       didFetchRef.current = true;
//     } else if (!currentUser || currentUser.role !== 'admin') {
//       setLoading(false); // Stop loading if unauthorized
//     }
//   }, [currentUser, fetchAdminData, fetchAllDeviceEvents, fetchLiveData, didFetchRef]);

//   // --- Polling useEffect ---
//   useEffect(() => {
//     if (!currentUser || currentUser.role !== 'admin') return; 

//     const poll = async () => {
//       if (skipFetch) return;
      
//       const token = localStorage.getItem("token");
//       if (!token) { handleLogout(); return; }
//       const config = { headers: { "x-auth-token": token } };
//       const promises = [];

//       // 1. Device events polling
//       promises.push(
//         (async () => {
//           try {
//             const response = await axios.get(`${backendUrl}/devices/events`, config);
//             const allEvents = response.data;
//             const flattenedEvents = allEvents.flatMap(device => device.events.map(event => ({ ...event, imei: device.imei, deviceName: device.deviceName })));
//             flattenedEvents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
//             const latestEvent = flattenedEvents[0];

//             if (
//                 latestEvent &&
//                 (!lastEventRef.current || lastEventRef.current.timestamp !== latestEvent.timestamp || lastEventRef.current.imei !== latestEvent.imei)
//             ) {
//               playEventSound();
//               lastEventRef.current = { timestamp: latestEvent.timestamp, imei: latestEvent.imei, };
//             }

//             if (!isEqual(allEvents, prevEventsRef.current)) {
//               prevEventsRef.current = allEvents;
//               setDeviceEvents(allEvents);
//             }

//           } catch (err) {
//             console.error("Error fetching device events:", err);
//             setError(err.response?.data?.msg || "Failed to load device events.");
//           }
//         })()
//       );

//       // 2. Map polling
//       if (activeTab === "map" && selectedDevices.length === 1) {
//         promises.push(
//           (async () => {
//             try {
//               const deviceId = selectedDevices[0];
//               const res = await axios.get(
//                 `${backendUrl}/devices/${deviceId}?fields=name,model,vehicleType,liveData,lastDataReceivedAt,geofences`,
//                 config
//               );
//               const device = res.data;

//               const currentLocation = device.liveData?.latitude && device.liveData?.longitude
//                   ? { latitude: device.liveData.latitude, longitude: device.liveData.longitude, timestamp: device.liveData.timestamp || new Date(), speed: device.liveData.speed || 0, }
//                   : null;

//               setDevicesOnMap(prev =>
//                 prev.map(d => (d._id === device._id ? { ...device, currentLocation } : d))
//               );
              
//             } catch (err) {
//               console.error("Error fetching map data:", err);
//               setError(err.response?.data?.msg || "Failed to load device map data.");
//             }
//           })()
//         );
//       } else if (activeTab === "map" && selectedDevices.length === 0) {
//         promises.push(fetchLiveData());
//       }
      
//       await Promise.allSettled(promises);
//     };

//     const intervalId = setInterval(poll, 10000); 
//     return () => clearInterval(intervalId);
//   }, [currentUser, activeTab, selectedDevices, devicesOnMap, handleLogout, fetchLiveData, prevEventsRef, playEventSound, skipFetch]); 

//   // --- Geocoding Logic ---
//   useEffect(() => {
//     if (!currentUser || currentUser.role !== 'admin') return;

//     devicesOnMap.forEach((device) => {
//       if (!device.currentLocation) return;
//       const { latitude: lat, longitude: lng } = device.currentLocation;
//       if (!lat || !lng) return;

//       axios.get(`${backendUrl}/geocode/reverse?lat=${lat}&lon=${lng}`)
//         .then((res) => {
//           setDeviceAddresses((prev) => ({
//             ...prev,
//             [device._id]: res.data.address || "Unknown address",
//           }));
//         })
//         .catch((err) => {
//           console.error("Failed to fetch address for device", device._id, err);
//           setDeviceAddresses((prev) => ({
//             ...prev,
//             [device._id]: "Unknown address",
//           }));
//         });
//     });
//   }, [devicesOnMap, setDeviceAddresses, currentUser]);

//   return {
//     loading,
//     error,
//     users,
//     devices,
//     devicesOnMap,
//     deviceCounts,
//     deviceEvents,
//     eventLoading,
//     eventError,
//     assetsData,
//     isAssetsLoading,
//     assetError,
//     editingUser,
//     setEditingUser,
//     fetchAdminData,
//     fetchLiveData,
//     fetchAllAssets,
//     fetchAllDeviceEvents,
//   };
// }

import { useState, useEffect, useRef, useCallback, startTransition } from 'react';
import axios from 'axios';
import isEqual from 'lodash.isequal';
import {
  fetchDeviceEvents,
  normalizeDeviceLivePayload,
} from '../../utils/apiHelpers';
import {
  useLazyGetAllLiteDevicesQuery,
  useLazyGetMyDevicesQuery,
} from '../../redux/admin/adminApi';
import {
  useGetDashboardSummaryQuery,
  useGetAllUsersQuery,
} from '../../redux/admin/dashboardApi';
import {
  useLazyGetDeviceV2ChangesQuery,
  useLazyGetDeviceV2LatestStatusQuery,
  useLazyGetDevicesV2PageQuery,
} from '../../redux/admin/deviceApi';
import { hasUserPermission } from '../../utils/rbacHelper';
import { patchLegacyDeviceWithV2Delta } from '../../utils/deviceV2Adapter';
import { readDeviceViewCache, writeDeviceViewCache } from '../../utils/deviceViewCache';
import { boundsToQueryParam } from '../../utils/mapClustering';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const MAP_INITIAL_PAGE_SIZE = 100;
const MAP_BACKGROUND_PAGE_SIZE = 200;
const MAP_BACKGROUND_DEVICE_CAP = 5000;
const MAP_SNAPSHOT_CACHE_TTL_MS = 2 * 60 * 1000;
const MAP_SNAPSHOT_CACHE_VERSION = 1;
const SIDEBAR_DEVICE_CACHE_SCOPE = "admin-sidebar-devices";

const normalizeRole = (role) => {
  const r = String(role || "").trim().toLowerCase();
  if (r === "superadmin" || r === "super_admin") return "admin";
  if (r === "groupadmin" || r === "dealer") return "group_admin";
  if (r === "subadmin") return "sub_admin";
  if (r === "officeemployee") return "office_emp";
  if (r === "fieldemployee") return "field_emp";
  return r;
};

const buildMapSnapshotCacheKey = (userId) =>
  `gps:map-snapshot:v${MAP_SNAPSHOT_CACHE_VERSION}:${String(userId || "anonymous")}`;

const readMapSnapshotCache = (userId) => {
  try {
    if (typeof localStorage === "undefined" || !userId) return null;
    const raw = localStorage.getItem(buildMapSnapshotCacheKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const createdAt = Number(parsed?.createdAt || 0);
    if (!createdAt || Date.now() - createdAt > MAP_SNAPSHOT_CACHE_TTL_MS) {
      localStorage.removeItem(buildMapSnapshotCacheKey(userId));
      return null;
    }
    const devices = Array.isArray(parsed?.devices) ? parsed.devices : [];
    return {
      devices,
      counts: parsed?.counts && typeof parsed.counts === "object" ? parsed.counts : null,
      totalDeviceCount: Number(parsed?.totalDeviceCount || devices.length || 0),
    };
  } catch (_) {
    return null;
  }
};

const writeMapSnapshotCache = (userId, payload) => {
  try {
    if (typeof localStorage === "undefined" || !userId) return;
    const devices = Array.isArray(payload?.devices) ? payload.devices : [];
    localStorage.setItem(
      buildMapSnapshotCacheKey(userId),
      JSON.stringify({
        createdAt: Date.now(),
        devices,
        counts: payload?.counts || null,
        totalDeviceCount: Number(payload?.totalDeviceCount || devices.length || 0),
      })
    );
  } catch (_) {
    // Ignore storage quota / serialization failures.
  }
};

const useAdminData = ({
  currentUser,
  handleLogout,
  prevEventsRef,
  didFetchRef,
  setSelectedDevices,
  activeTab,
  selectedDevices,
  audioRef,
  skipFetch = false,
}) => {
  const shouldFetchUsers =
    !skipFetch && Boolean(currentUser) && ["users", "assignments"].includes(activeTab);
  const shouldFetchDeviceOptions =
    !skipFetch && Boolean(currentUser) && ["users", "assignments", "map"].includes(activeTab);

  const {
    data: dashboardSummary,
    refetch: refetchSummary,
  } = useGetDashboardSummaryQuery(undefined, {
    skip: skipFetch || !currentUser,
  });
  const {
    data: usersFromAPI = [],
    refetch: refetchUsers,
  } = useGetAllUsersQuery(undefined, {
    skip: !shouldFetchUsers,
  });
  const [loadAllLiteDevices] = useLazyGetAllLiteDevicesQuery();
  const [loadMyDevices] = useLazyGetMyDevicesQuery();
  const [loadDevicesV2Page] = useLazyGetDevicesV2PageQuery();
  const [loadDeviceV2Changes] = useLazyGetDeviceV2ChangesQuery();
  const [loadDeviceV2LatestStatus] = useLazyGetDeviceV2LatestStatusQuery();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]); // Shared lightweight device rows for sidebar/options flows.
  const [devicesOnMap, setDevicesOnMap] = useState([]);
  const [progressiveLoading, setProgressiveLoading] = useState({
    runningIdle: false,
    stopped: false,
    inactive: false,
  });
  
  const [deviceCounts, setDeviceCounts] = useState({
    total: 0, online: 0, offline: 0, unknown: 0, no_data: 0
  });
  const [totalDeviceCount, setTotalDeviceCount] = useState(0);
  
  // Event & Asset States
  const [deviceEvents, setDeviceEvents] = useState([]);
  const [eventLoading, setEventLoading] = useState(false);
  const [eventError, setEventError] = useState(null);
  const [assetsData, setAssetsData] = useState([]);
  const [isAssetsLoading, setIsAssetsLoading] = useState(false);
  const [assetError, setAssetError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  
  const lastEventRef = useRef(null);
  const previousUserIdRef = useRef("");
  const mapRequestRef = useRef(0);
  const hasHydratedMapCacheRef = useRef(false);
  const lastDeltaSinceRef = useRef(null);
  const mapViewportRef = useRef(null);
  const isSoftDeletedDevice = useCallback(
    (device) => String(device?.status || "").toLowerCase() === "deleted",
    []
  );

  const transformDevicesForMap = useCallback((devicesData) => {
    if (!Array.isArray(devicesData)) return [];

    const toAccBoolean = (value) => {
      if (value === null || value === undefined || value === "") return null;
      if (typeof value === "boolean") return value;
      if (typeof value === "number") return value !== 0;
      if (typeof value === "string") {
        const lowered = value.trim().toLowerCase();
        if (["1", "true", "on", "yes", "acc_on"].includes(lowered)) return true;
        if (["0", "false", "off", "no", "acc_off"].includes(lowered)) return false;
      }
      return null;
    };
    
    return devicesData
      .filter((device) => !isSoftDeletedDevice(device))
      .map(device => {
        if (!device) return null;

        const live = device.deviceLive || device.liveSnapshot || {};
        const lat = Number(
          live.latitude ??
          live.lat ??
          device.currentLocation?.latitude ??
          device.liveData?.latitude
        );
        const lng = Number(
          live.longitude ??
          live.lng ??
          device.currentLocation?.longitude ??
          device.liveData?.longitude
        );
        const hasValidGps =
          Number.isFinite(lat) &&
          Number.isFinite(lng) &&
          lat !== 0 &&
          lng !== 0;

        const currentLocation = hasValidGps
          ? {
              latitude: lat,
              longitude: lng,
              timestamp:
                live.timestamp ||
                device.currentLocation?.timestamp ||
                device.liveData?.timestamp ||
                device.lastDataReceivedAt ||
                new Date(),
              speed: Number(live.speed ?? device.liveData?.speed) || 0,
              heading: Number(live.heading ?? device.liveData?.heading) || 0,
              accStatus: toAccBoolean(live.accStatus ?? device.liveData?.accStatus),
              vehicleStatus: live.vehicleStatus ?? device.liveData?.vehicleStatus ?? device.vehicleStatus ?? "no_data",
            }
          : null;
        
        return {
          ...device,
          deviceLive: live,
          liveData: device.liveData || {},
          currentLocation,
          hasValidGps
        };
      })
      .filter(device => device !== null);
  }, [isSoftDeletedDevice]);

  const mergeDeviceList = useCallback((prevDevices, nextDevices) => {
    const merged = new Map();
    (Array.isArray(prevDevices) ? prevDevices : []).forEach((device) => {
      if (device?._id) merged.set(String(device._id), device);
    });
    (Array.isArray(nextDevices) ? nextDevices : []).forEach((device) => {
      if (!device?._id) return;
      merged.set(String(device._id), device);
    });
    return Array.from(merged.values());
  }, []);

  const mergeIntoSidebarDevices = useCallback((nextDevices) => {
    if (!Array.isArray(nextDevices) || nextDevices.length === 0) return;

    setDevices((prevDevices) => {
      const previousRows = Array.isArray(prevDevices) ? prevDevices : [];
      return mergeDeviceList(previousRows, nextDevices);
    });
  }, [mergeDeviceList]);

  useEffect(() => {
    if (skipFetch || !currentUser?._id) return;
    if (activeTab !== "map" && !shouldFetchDeviceOptions) return;
    if (devices.length > 0) return;

    const cachedPayload = readDeviceViewCache(SIDEBAR_DEVICE_CACHE_SCOPE, {
      userId: currentUser._id,
      params: { scope: "shared" },
    });

    const cachedRows = Array.isArray(cachedPayload?.devices) ? cachedPayload.devices : [];
    if (!cachedRows.length) return;

    setDevices(cachedRows.map((device) => normalizeDeviceLivePayload(device)));
  }, [
    activeTab,
    currentUser?._id,
    devices.length,
    shouldFetchDeviceOptions,
    skipFetch,
  ]);

  useEffect(() => {
    if (skipFetch || !currentUser?._id) return;
    if (!Array.isArray(devices) || !devices.length) return;
    if (activeTab !== "map" && !shouldFetchDeviceOptions) return;

    writeDeviceViewCache(SIDEBAR_DEVICE_CACHE_SCOPE, {
      userId: currentUser._id,
      params: { scope: "shared" },
      payload: {
        devices,
      },
    });
  }, [
    activeTab,
    currentUser?._id,
    devices,
    shouldFetchDeviceOptions,
    skipFetch,
  ]);

  const fetchAllLiteDevicesForSidebar = useCallback(async () => {
    const currentRole = normalizeRole(currentUser?.role);
    if (["user", "driver", "client", "viewer"].includes(currentRole)) {
      const payload = await loadMyDevices({ includeExpired: "true" }, true).unwrap();
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.devices)
            ? payload.devices
            : [];
      return rows;
    }

    const response = await loadAllLiteDevices({ includeExpired: "true" }, true).unwrap();
    return Array.isArray(response) ? response : [];
  }, [currentUser?.role, loadAllLiteDevices, loadMyDevices]);

  const recalculateDeviceState = useCallback((devicesData, preferLoadedTotals = false) => {
    const visibleDevices = (Array.isArray(devicesData) ? devicesData : []).filter(
      (device) => !isSoftDeletedDevice(device)
    );
    const counts = {
      total: visibleDevices.length,
      online: visibleDevices.filter((d) =>
        d?.status?.toLowerCase() === 'online' ||
        d?.status?.toLowerCase() === 'moving'
      ).length,
      offline: visibleDevices.filter((d) => d?.status?.toLowerCase() === 'offline').length,
      unknown: visibleDevices.filter((d) => !d?.status || d.status?.toLowerCase() === 'unknown').length,
    };

    if (preferLoadedTotals || !dashboardSummary?.counts) {
      setDeviceCounts(counts);
      setTotalDeviceCount((prev) => Math.max(Number(prev) || 0, visibleDevices.length));
    }
    setDevicesOnMap(transformDevicesForMap(visibleDevices));

    if (currentUser?._id) {
      const persistedCounts = dashboardSummary?.counts
        ? {
            total: Number(dashboardSummary.counts.total || 0),
            online: Number(dashboardSummary.counts.online || 0),
            offline: Number(dashboardSummary.counts.offline || 0),
            unknown: Math.max(
              0,
              Number(dashboardSummary.counts.total || 0) -
                Number(dashboardSummary.counts.online || 0) -
                Number(dashboardSummary.counts.offline || 0)
            ),
            running: Number(dashboardSummary.counts.running || 0),
            idle: Number(dashboardSummary.counts.idle || 0),
            stopped: Number(dashboardSummary.counts.stopped || 0),
            inactive: Number(dashboardSummary.counts.inactive || 0),
            no_data: Number(dashboardSummary.counts.no_data || 0),
            expired: Number(dashboardSummary.counts.expired || 0),
          }
        : counts;
      writeMapSnapshotCache(currentUser._id, {
        devices: visibleDevices,
        counts: persistedCounts,
        totalDeviceCount: dashboardSummary?.counts
          ? Number(dashboardSummary.counts.total || visibleDevices.length || 0)
          : Math.max(Number(totalDeviceCount) || 0, visibleDevices.length),
      });
      writeDeviceViewCache(SIDEBAR_DEVICE_CACHE_SCOPE, {
        userId: currentUser._id,
        params: { scope: "shared" },
        payload: {
          devices: visibleDevices,
        },
      });
    }
  }, [
    currentUser?._id,
    dashboardSummary?.counts,
    isSoftDeletedDevice,
    totalDeviceCount,
    transformDevicesForMap,
  ]);

  const fetchMapData = useCallback(async () => {
    if (skipFetch || !currentUser) return;

    const requestId = mapRequestRef.current + 1;
    mapRequestRef.current = requestId;
    setError(null);
    setProgressiveLoading({
      runningIdle: true,
      stopped: false,
      inactive: false,
    });

    try {
      const mapBoundsParam = boundsToQueryParam(mapViewportRef.current?.bounds);
      const firstPage = await loadDevicesV2Page(
        {
          page: 1,
          limit: MAP_INITIAL_PAGE_SIZE,
          ...(mapBoundsParam ? { bounds: mapBoundsParam } : {}),
        },
        false
      ).unwrap();

      if (mapRequestRef.current !== requestId) return;

      const initialRows = (Array.isArray(firstPage?.data) ? firstPage.data : []).map(
        (device) => normalizeDeviceLivePayload(device)
      );

      startTransition(() => {
        recalculateDeviceState(initialRows, !dashboardSummary?.counts);
        mergeIntoSidebarDevices(initialRows);
      });

      let mergedRows = initialRows;
      let nextPage = Number(firstPage?.page || 1);
      let hasMore = Boolean(firstPage?.hasMore);
      const totalAvailable = Number(firstPage?.total || mergedRows.length) || mergedRows.length;
      const targetCount = Math.min(totalAvailable, MAP_BACKGROUND_DEVICE_CAP);

      while (hasMore && mergedRows.length < targetCount && mapRequestRef.current === requestId) {
        const nextResult = await loadDevicesV2Page(
          {
            page: nextPage + 1,
            limit: MAP_BACKGROUND_PAGE_SIZE,
            ...(mapBoundsParam ? { bounds: mapBoundsParam } : {}),
          },
          false
        ).unwrap();

        if (mapRequestRef.current !== requestId) return;

        const nextRows = (Array.isArray(nextResult?.data) ? nextResult.data : []).map((device) =>
          normalizeDeviceLivePayload(device)
        );
        mergedRows = mergeDeviceList(mergedRows, nextRows);
        nextPage = Number(nextResult?.page || nextPage + 1);
        hasMore = Boolean(nextResult?.hasMore);

        const mergedRowsSnapshot = mergedRows;
        startTransition(() => {
          recalculateDeviceState(mergedRowsSnapshot, !dashboardSummary?.counts);
          mergeIntoSidebarDevices(mergedRowsSnapshot);
        });

        setProgressiveLoading({
          runningIdle: true,
          stopped: hasMore,
          inactive: mergedRows.length < targetCount,
        });
      }

      lastDeltaSinceRef.current = new Date().toISOString();
    } catch (err) {
      console.error("Error fetching map data:", err);
      if (err?.status === 401 || err?.originalStatus === 401) {
        handleLogout();
      } else {
        setError(err?.data?.msg || err?.message || "Failed to load map data");
      }
    } finally {
      if (mapRequestRef.current === requestId) {
        setProgressiveLoading({
          runningIdle: false,
          stopped: false,
          inactive: false,
        });
      }
    }
  }, [
    currentUser,
    dashboardSummary?.counts,
    handleLogout,
    loadDevicesV2Page,
    mergeDeviceList,
    mergeIntoSidebarDevices,
    recalculateDeviceState,
    skipFetch,
  ]);

  const updateMapViewport = useCallback((nextViewport) => {
    if (!nextViewport?.bounds) return;
    const previous = mapViewportRef.current;
    const nextZoom = Math.round(Number(nextViewport.zoom || 0));
    const nextBounds = nextViewport.bounds;
    const previousBounds = previous?.bounds;
    const changed =
      !previousBounds ||
      Math.abs(Number(previousBounds.north || 0) - Number(nextBounds.north || 0)) > 0.05 ||
      Math.abs(Number(previousBounds.south || 0) - Number(nextBounds.south || 0)) > 0.05 ||
      Math.abs(Number(previousBounds.east || 0) - Number(nextBounds.east || 0)) > 0.05 ||
      Math.abs(Number(previousBounds.west || 0) - Number(nextBounds.west || 0)) > 0.05 ||
      Math.round(Number(previous?.zoom || 0)) !== nextZoom;

    if (!changed) return;
    mapViewportRef.current = {
      zoom: nextZoom,
      bounds: nextBounds,
      center: nextViewport.center || null,
    };

if (activeTab === "map" && currentUser && !skipFetch && devicesOnMap.length === 0) {
  fetchMapData();
}
}, [activeTab, currentUser, fetchMapData, skipFetch, devicesOnMap.length]);

  useEffect(() => {
    const nextUserId = String(currentUser?._id || currentUser?.id || "");
    if (previousUserIdRef.current === nextUserId) return;

    previousUserIdRef.current = nextUserId;
    mapRequestRef.current += 1;
    hasHydratedMapCacheRef.current = false;
    lastDeltaSinceRef.current = null;
    prevEventsRef.current = undefined;
    lastEventRef.current = null;

    setUsers([]);
    setDevices([]);
    setDevicesOnMap([]);
    setAssetsData([]);
    setDeviceEvents([]);
    setEditingUser(null);
    setError(null);
    setAssetError(null);
    setEventError(null);
    setDeviceCounts({
      total: 0,
      online: 0,
      offline: 0,
      unknown: 0,
      no_data: 0,
    });
    setTotalDeviceCount(0);
    setProgressiveLoading({
      runningIdle: false,
      stopped: false,
      inactive: false,
    });
    setSelectedDevices?.([]);
  }, [currentUser?._id, currentUser?.id, prevEventsRef, setSelectedDevices]);

  useEffect(() => {
    if (Array.isArray(usersFromAPI)) {
      const normalizedUsers = usersFromAPI.map((u) => ({
        ...u,
        role: normalizeRole(u?.role),
      }));
      setUsers(normalizedUsers);
    }
  }, [usersFromAPI]);

  useEffect(() => {
    if (!dashboardSummary?.counts) return;
    const counts = dashboardSummary.counts || {};
    const total = Number(counts.total || 0);
    const online = Number(counts.online || 0);
    const offline = Number(counts.offline || 0);
    setDeviceCounts({
      total,
      online,
      offline,
      unknown: Math.max(0, total - online - offline),
      running: Number(counts.running || 0),
      idle: Number(counts.idle || 0),
      stopped: Number(counts.stopped || 0),
      inactive: Number(counts.inactive || 0),
      no_data: Number(counts.no_data || 0),
      expired: Number(counts.expired || 0),
    });
    setTotalDeviceCount(total);
  }, [dashboardSummary]);

  useEffect(() => {
    let cancelled = false;

    const loadDeviceOptions = async () => {
      if (!shouldFetchDeviceOptions) return;
      try {
        const rows = await fetchAllLiteDevicesForSidebar();
        if (cancelled) return;
        setDevices(Array.isArray(rows) ? rows.map((device) => normalizeDeviceLivePayload(device)) : []);
      } catch (err) {
        if (!cancelled) {
          console.warn("Device options load failed:", err?.message || err);
        }
      }
    };

    loadDeviceOptions();
    return () => {
      cancelled = true;
    };
  }, [fetchAllLiteDevicesForSidebar, shouldFetchDeviceOptions]);

  useEffect(() => {
    if (shouldFetchDeviceOptions || activeTab === "map") return;
    setDevices([]);
  }, [activeTab, shouldFetchDeviceOptions]);

  // Live data polling
  const fetchLiveData = useCallback(async () => {
    if (skipFetch || !currentUser || activeTab !== "map") return;
    
    try {
      if (!selectedDevices.length) return;

      const snapshots = await Promise.allSettled(
        selectedDevices.slice(0, 10).map((deviceId) => loadDeviceV2LatestStatus(deviceId, true).unwrap())
      );
      const updatedDevices = snapshots
        .filter((entry) => entry.status === "fulfilled" && entry.value?._id)
        .map((entry) => entry.value);

      if (!updatedDevices.length) return;

      setDevicesOnMap((prev) => {
        const merged = mergeDeviceList(prev, updatedDevices);
        recalculateDeviceState(merged, !dashboardSummary?.counts);
        return merged;
      });
    } catch (err) {
      console.warn("Live data poll failed:", err.message);
    }
  }, [
    activeTab,
    currentUser,
    dashboardSummary?.counts,
    loadDeviceV2LatestStatus,
    mergeDeviceList,
    recalculateDeviceState,
    selectedDevices,
    skipFetch,
  ]);

  // Event fetching with alarm processing
  const fetchEvents = useCallback(async () => {
    if (skipFetch || !currentUser) return;
    
    try {
      if (!prevEventsRef.current) setEventLoading(true);
      
      const eventsData = await fetchDeviceEvents();
      const flattenedEvents = (Array.isArray(eventsData) ? eventsData : []).flatMap((device) =>
        (device?.events || device?.eventHistory || []).map((event) => ({
          ...event,
          imei: device?.imei,
          deviceName: device?.deviceName || device?.name,
        }))
      );
      flattenedEvents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      const latestEvent = flattenedEvents[0];

      if (
        latestEvent &&
        (!lastEventRef.current ||
          lastEventRef.current.timestamp !== latestEvent.timestamp ||
          lastEventRef.current.imei !== latestEvent.imei)
      ) {
        if (audioRef.current) audioRef.current.play().catch(() => {});
        lastEventRef.current = { timestamp: latestEvent.timestamp, imei: latestEvent.imei };
      }
      
      if (!isEqual(eventsData, prevEventsRef.current)) {
        prevEventsRef.current = eventsData;
        setDeviceEvents(eventsData);
      }
      
      setEventLoading(false);
    } catch (err) {
      setEventLoading(false);
      
      if (err.response?.status === 401) {
        handleLogout();
      } else if (err.response?.status === 403) {
        setEventError(err.response?.data?.msg || "Access denied");
      } else {
        setEventError("Failed to load events");
      }
    }
  }, [skipFetch, currentUser, prevEventsRef, handleLogout]);

  // Assets fetcher
  const fetchAllAssets = useCallback(async () => {
    if (skipFetch || !currentUser) return;
    
    try {
      setIsAssetsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/devices/assets`, { 
        headers: { 'x-auth-token': token } 
      });
      setAssetsData(Array.isArray(response.data.data) ? response.data.data : []);
      setIsAssetsLoading(false);
    } catch (err) {
      setIsAssetsLoading(false);
      
      if (err.response?.status === 403) {
        setAssetError(err.response?.data?.msg || "Access denied");
      } else if (err.response?.status === 401) {
        handleLogout();
      } else {
        setAssetError("Failed to load assets");
      }
    }
  }, [skipFetch, currentUser, handleLogout]);

  const fetchDeltaChanges = useCallback(async () => {
    if (skipFetch || !currentUser || activeTab !== "map") return;
    if (!lastDeltaSinceRef.current) return;

    try {
      const payload = await loadDeviceV2Changes(
        { since: lastDeltaSinceRef.current, limit: 200 },
        true
      ).unwrap();

      const rows = Array.isArray(payload?.data) ? payload.data : [];
      if (!rows.length) {
        if (payload?.nextSince) lastDeltaSinceRef.current = payload.nextSince;
        return;
      }

      const deltaById = new Map(
        rows
          .map((item) => [String(item?.deviceId || item?._id || item?.id || ""), item])
          .filter(([id]) => id)
      );

      const applyDeltaRows = (prevRows) =>
        (Array.isArray(prevRows) ? prevRows : []).map((device) => {
          const id = String(device?._id || device?.id || "");
          return deltaById.has(id)
            ? patchLegacyDeviceWithV2Delta(device, deltaById.get(id))
            : device;
        });

      setDevicesOnMap((prev) => {
        const next = applyDeltaRows(prev);
        recalculateDeviceState(next, !dashboardSummary?.counts);
        return next;
      });
      setDevices((prev) => applyDeltaRows(prev));

      lastDeltaSinceRef.current = payload?.nextSince || new Date().toISOString();
    } catch (err) {
      console.warn("Device delta poll failed:", err?.message || err);
    }
  }, [
    activeTab,
    currentUser,
    dashboardSummary?.counts,
    loadDeviceV2Changes,
    recalculateDeviceState,
    skipFetch,
  ]);

  const fetchAdminData = useCallback(async () => {
    if (skipFetch || !currentUser) {
      setLoading(false);
      return;
    }

    setLoading(false);
    setError(null);

    try {
      await refetchSummary();

      if (activeTab === "map") {
        await fetchMapData();
      }

      if (activeTab === "events" || activeTab === "map") {
        await fetchEvents();
      }

      if (shouldFetchUsers) {
        await refetchUsers();
      }

      if (shouldFetchDeviceOptions) {
        const rows = await fetchAllLiteDevicesForSidebar();
        setDevices(Array.isArray(rows) ? rows.map((device) => normalizeDeviceLivePayload(device)) : []);
      } else if (activeTab !== "map") {
        setDevices([]);
      }

      if (activeTab === "assignments" && hasUserPermission(currentUser, 'viewAssets')) {
        await fetchAllAssets();
      }
    } catch (err) {
      console.error("Error refreshing admin data:", err);
      const syncMessage =
        err?.response?.data?.msg ||
        err?.response?.data?.message ||
        err?.data?.msg ||
        err?.data?.message ||
        err?.message ||
        "";
      if (syncMessage && syncMessage !== "System sync failed") {
        setError(syncMessage);
      }
    }
  }, [
    activeTab,
    currentUser,
    fetchAllAssets,
    fetchEvents,
    fetchMapData,
    refetchSummary,
    refetchUsers,
    activeTab,
    shouldFetchDeviceOptions,
    shouldFetchUsers,
    skipFetch,
    fetchAllLiteDevicesForSidebar,
  ]);

  useEffect(() => {
    if (!skipFetch && currentUser) {
      didFetchRef.current = true;
      setLoading(false);
    }
  }, [currentUser, didFetchRef, skipFetch]);

  useEffect(() => {
    if (activeTab !== "map" || skipFetch || !currentUser?._id) return;
    if (hasHydratedMapCacheRef.current) return;

    const cachedSnapshot = readMapSnapshotCache(currentUser._id);
    if (!cachedSnapshot?.devices?.length) return;

    hasHydratedMapCacheRef.current = true;
    const normalizedRows = cachedSnapshot.devices.map((device) => normalizeDeviceLivePayload(device));
    startTransition(() => {
      setDevicesOnMap(transformDevicesForMap(normalizedRows));
      setDevices((prevDevices) => {
        const previousRows = Array.isArray(prevDevices) ? prevDevices : [];
        return previousRows.length ? mergeDeviceList(previousRows, normalizedRows) : normalizedRows;
      });
      if (cachedSnapshot?.counts) {
        setDeviceCounts((prev) => ({
          ...prev,
          ...cachedSnapshot.counts,
        }));
      }
      if (cachedSnapshot?.totalDeviceCount) {
        setTotalDeviceCount(Number(cachedSnapshot.totalDeviceCount) || normalizedRows.length);
      }
    });
  }, [activeTab, currentUser?._id, mergeDeviceList, skipFetch, transformDevicesForMap]);

  useEffect(() => {
    if (activeTab === "map") return undefined;
    hasHydratedMapCacheRef.current = false;
    lastDeltaSinceRef.current = null;
    return undefined;
  }, [activeTab]);

  useEffect(() => {
    if (skipFetch || !currentUser) return;

    if (activeTab === "map") {
      fetchMapData();
      fetchEvents();
      return;
    }

    if (activeTab === "events") {
      fetchEvents();
      return;
    }

    if (activeTab === "assignments" && hasUserPermission(currentUser, 'viewAssets')) {
      fetchAllAssets();
    }
  }, [activeTab, currentUser, fetchAllAssets, fetchEvents, fetchMapData, skipFetch]);

  useEffect(() => {
    if (activeTab === "map") return undefined;
    mapRequestRef.current += 1;
    setProgressiveLoading({
      runningIdle: false,
      stopped: false,
      inactive: false,
    });
    return undefined;
  }, [activeTab]);

  // Global CRUD sync hook:
  // when any add/update/delete/assign mutation succeeds, refresh current dashboard data.
  useEffect(() => {
    if (skipFetch || !currentUser) return undefined;

    const handleCrudChanged = () => {
      fetchAdminData();
      fetchEvents();
      if (hasUserPermission(currentUser, 'viewAssets')) fetchAllAssets();
    };

    window.addEventListener("app:crud-changed", handleCrudChanged);
    return () => window.removeEventListener("app:crud-changed", handleCrudChanged);
  }, [skipFetch, currentUser, fetchAdminData, fetchEvents, fetchAllAssets]);

  // Polling effect
  useEffect(() => {
    if (skipFetch || !currentUser) return;
    
    const interval = setInterval(() => {
      fetchLiveData();
      if (activeTab === "map") fetchDeltaChanges();
      if (activeTab === 'events' || activeTab === "map") fetchEvents();
    }, 15000);
    
    return () => clearInterval(interval);
  }, [skipFetch, fetchLiveData, fetchDeltaChanges, fetchEvents, activeTab, currentUser]);

  // Geocoding effect
  // useEffect(() => {
  //   if (!currentUser || devicesOnMap.length === 0) return;
    
  //   const fetchAddresses = async () => {
  //     const newAddresses = {};
      
  //     for (const device of devicesOnMap) {
  //       if (deviceAddresses[device._id]) continue;
        
  //       const lat = device.liveData?.latitude || device.currentLocation?.latitude;
  //       const lon = device.liveData?.longitude || device.currentLocation?.longitude;
        
  //       if (typeof lat !== 'number' || typeof lon !== 'number') continue;

  //       try {
  //         const response = await fetch(
  //           `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`,
  //           { headers: { 'User-Agent': 'VehicleTrackingApp/1.0' } }
  //         );
          
  //         if (!response.ok) throw new Error('Geocoding failed');
          
  //         const data = await response.json();
  //         newAddresses[device._id] = data?.display_name || 'Address not found';
  //       } catch (err) {
  //         console.warn(`Geocoding failed for ${device.name}:`, err.message);
  //         newAddresses[device._id] = 'Address unavailable';
  //       }
        
  //       await new Promise(resolve => setTimeout(resolve, 1100));
  //     }
      
  //     if (Object.keys(newAddresses).length > 0) {
  //       setDeviceAddresses(prev => ({ ...prev, ...newAddresses }));
  //     }
  //   };

  //   const timeoutId = setTimeout(fetchAddresses, 1000);
  //   return () => clearTimeout(timeoutId);
  // }, [devicesOnMap, currentUser]);

  // ✅ CRITICAL: Return guaranteed arrays, never undefined
  return {
    loading, 
    error, 
    users: Array.isArray(users) ? users : [],
    devices: Array.isArray(devices) ? devices : [],
    devicesOnMap: Array.isArray(devicesOnMap) ? devicesOnMap : [],
    deviceCounts,
    totalDeviceCount,
    progressiveLoading,
    // deviceAddresses,
    // setDeviceAddresses,
    deviceEvents: Array.isArray(deviceEvents) ? deviceEvents : [],
    eventLoading, 
    eventError,
    assetsData: Array.isArray(assetsData) ? assetsData : [],
    isAssetsLoading, 
    assetError,
    editingUser, 
    setEditingUser,
    fetchAdminData, 
    fetchAllAssets,
    dashboardSummary,
    updateMapViewport,
  };
};

export default useAdminData;
